/**
 * إخلاء المسؤولية — الصفحة الكاملة.
 *
 * المكوّن `<Disclaimer />` يظهر تحت كل نتيجة بجملة واحدة؛ هذه الصفحة تشرحها.
 * والنصّ المختصر يُستورَد من مصدره الواحد لا يُعاد كتابته هنا: نسختان من
 * إخلاء مسؤولية تفترقان يوماً ما، وحين تفترقان لا نعرف أيّهما الملزِمة.
 *
 * وفيها ما لا تضعه المنتجات عادةً: **حدود التغطية الفعلية بأرقامها**. من يخفي
 * ثغرات بياناته يكسب ثقةً اليوم ويخسرها كلها يوم يكتشفها المستخدم بنفسه.
 */

import { Link } from 'react-router-dom';
import { color } from '@/design/tokens';
import { DISCLAIMER_TEXT } from '@/components/data/Disclaimer';
import { LegalPage, LegalSection, L, Notice, P } from './LegalPage';

/**
 * لا أرقام حيّة هنا عمداً: الصفحة النظامية تصف **الحدود** لا الحالة، وربطها
 * بطبقة البيانات يجعلها تفشل حين تفشل البيانات — وأسوأ وقت لاختفاء إخلاء
 * المسؤولية هو وقت العطل. الأرقام الحيّة مكانها الشاشات، والحدود مكانها هنا.
 */
export default function DisclaimerRoute(): JSX.Element {
  return (
    <LegalPage
      stamp="NITAQ / DISCLAIMER"
      title="إخلاء المسؤولية"
      subtitle="ما تعنيه أرقام نطاق بالضبط، وما لا تعنيه."
      testId="disclaimer-page"
    >
      <Notice>{DISCLAIMER_TEXT}</Notice>

      <LegalSection number="١" title="ما هذه الأرقام">
        <P>
          كل رقم في نطاق ناتج عن نماذج إحصائية تقرأ صفقات مسجّلة رسمياً وتقارن العقار
          بأشباهها في نفس الحي، بعد ترجيح المقارنات بقربها في المساحة والزمن.
        </P>
        <P>
          النتيجة <strong style={{ color: color.text }}>نطاق سعري</strong> لا رقم واحد،
          لأن السوق نفسه ليس رقماً واحداً. وضيق النطاق أو اتساعه يقول شيئاً عن السوق: كلما
          اتّسع، قلّ اتفاق الصفقات في ذلك الحي.
        </P>
      </LegalSection>

      <LegalSection number="٢" title="ما ليست هذه الأرقام">
        <L
          items={[
            'ليست تقرير تقييم معتمد، ولا تحلّ محلّه.',
            'لا تصلح للأغراض البنكية أو التمويلية أو القضائية أو النظامية.',
            'ليست توصية بشراء أو بيع، ولا مشورة استثمارية.',
            'ليست سعراً مضموناً — لا للبائع ولا للمشتري.',
            'لا تعرف خصائص العقار الفعلية: تسويتها، وارتفاعها، وقيود صكّها، وما جاورها.',
          ]}
        />
        <P>
          للحصول على تقييم معتمد، راجع مقيّماً مرخّصاً من الهيئة السعودية للمقيمين
          المعتمدين.
        </P>
      </LegalSection>

      <LegalSection number="٣" title="متى تمتنع المنصّة عن إعطاء رقم">
        <P>
          حين لا تكفي الصفقات المماثلة أو يتباعد تشتّتها، تمتنع المنصّة وتقول ذلك بدل أن
          تعطي رقماً ضعيف السند.
        </P>
        <P>
          <strong style={{ color: color.text }}>الامتناع نتيجة مقصودة.</strong> رقمٌ بلا
          سند أسوأ من لا رقم، لأنه يُبنى عليه قرار.
        </P>
      </LegalSection>

      <LegalSection number="٤" title="حدود التغطية الحالية">
        <P>هذه حدود نعلنها عن أنفسنا، لا تُكتشف بعد الاعتماد عليها:</P>
        <L
          items={[
            <>
              التغطية الحالية: <strong style={{ color: color.text }}>الرياض وجدة</strong>{' '}
              فقط · <strong style={{ color: color.text }}>الأراضي السكنية والمباني السكنية</strong>{' '}
              · بيع فقط.
            </>,
            <>
              ليست كل الأحياء جاهزة للتقدير — الحي الذي لا تكفي صفقاته يظهر بحالته صريحةً
              لا مخفياً.
            </>,
            <>
              ثغرة معلومة في بيانات الرياض من <strong style={{ color: color.text }}>١ يناير
              إلى ٤ مايو ٢٠٢٦</strong> — أول صفقة مسجّلة عندنا للرياض هي ٥ مايو. ولأجلها
              لا نعرض «النمو» للرياض على الخريطة: مقارنة الفترتين تحتاج تاريخاً أطول ممّا
              لدينا، والرقم كان سيقيس حافّة البيانات لا حركة السوق.
            </>,
            <>
              مؤشر ضغط السوق يحتاج بيانات عروض متراكمة عبر الزمن، ويظهر «غير متاح» حتى
              تتوفّر.
            </>,
            <>
              البيانات تُحدَّث دورياً لا لحظياً: ما بيع أمس قد لا يكون قد سُجّل بعد.
            </>,
          ]}
        />
        <P>
          <span className="nq-micro">
            عدد الصفقات والأحياء المغطّاة وتاريخ آخر تحديث تظهر حيّةً في أعلى الصفحة
            الرئيسية.
          </span>
        </P>
      </LegalSection>

      <LegalSection number="٥" title="مصادر البيانات">
        <P>
          الصفقات من مصدر رسمي للتصرّفات العقارية المسجّلة، تُستورد كما هي بلا تعديل على
          قيمها. وما يُرفض منها في المعالجة يُرفض بسبب مُسجَّل لا بصمت.
        </P>
        <P>
          ولا تحتوي بيانات الصفقات أي أسماء أو هويات لأطرافها — انظر{' '}
          <Link to="/privacy" style={{ color: color.teal }}>
            سياسة الخصوصية
          </Link>
          .
        </P>
      </LegalSection>

      <LegalSection number="٦" title="مسؤوليتك قبل القرار">
        <P>
          نطاق تقرأ البيانات، وأنت تتّخذ القرار. قبل أي التزام مالي: عاين العقار، وتحقّق من
          صكّها ومخططها وقيودها، واستعن بمختصّ مرخّص.
        </P>
        <P>
          الاعتماد على مخرجات المنصّة وحدها في قرار استثماري يقع على مسؤوليتك — انظر{' '}
          <Link to="/terms" style={{ color: color.teal }}>
            الشروط والأحكام
          </Link>
          .
        </P>
      </LegalSection>
    </LegalPage>
  );
}
